import { FC, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { DateTimeField, SelectField } from '@mews-commander/form';
import { createZodFormResolver, FormCheckboxField, lt, useTimezone, useFormControls } from '@mews/framework';
import { Rate, RateType } from '@mews-commander/data-access';
import { WithServiceId } from '@mews-commander/types';
import { Stack } from '@optimus-web/core';
import { useCalendarService } from '@mews-commander/ui';
import { getCurrentEnterpriseReservationsByIds } from '@mews-commander/legacy-entities/src/selectors/reservations';
import { useGroupedRatesOptions } from '@mews-commander/legacy-entities/src/queries/rates';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { useCommanderSelector } from '@mews-commander/utils';
import { FormLayout } from '../../FormLayout';
import { IntervalAdjustmentFormKeys, IntervalAdjustmentFormSchema, IntervalAdjustmentFormValues } from './schema';
import { compareFormValuesForMultipleIntervals } from './utils';
import { useIntervalAdjustmentFormMinDate } from './useIntervalAdjustmentFormMinDate';
import { useChangeInterval } from './useChangeInterval';
import { CancellationFeeText } from './CancellationFeeText';
import { useReservationsCommonInterval } from './useReservationsComonInterval';
import { useDefaultRateIdForNewReservation } from './useDefaultRateIdForNewReservation';

interface IntervalAdjustmentFormProps extends WithServiceId {
    reservationIds: string[];
    onSuccess?: (createNewReservation: boolean) => void;
}

export const IntervalAdjustmentForm: FC<IntervalAdjustmentFormProps> = ({ reservationIds, serviceId, onSuccess }) => {
    const commonInterval = useReservationsCommonInterval(reservationIds);
    const defaultRateId = useDefaultRateIdForNewReservation(reservationIds, serviceId);

    const defaultValues = useMemo(
        () => ({
            [IntervalAdjustmentFormKeys.Arrival]: commonInterval.startUtc,
            [IntervalAdjustmentFormKeys.Departure]: commonInterval.endUtc,
            [IntervalAdjustmentFormKeys.ApplyCancellationFee]: false,
            [IntervalAdjustmentFormKeys.CreateNewReservation]: false,
            [IntervalAdjustmentFormKeys.CreateNewReservationRateId]: defaultRateId,
        }),
        [commonInterval, defaultRateId]
    );
    const minDate = useIntervalAdjustmentFormMinDate(serviceId);
    const [interval, setInterval] = useState<{ startUtc: string; endUtc: string } | null>(null);

    const { changeInterval, dialog, isPending, loading } = useChangeInterval({
        commonInterval,
        reservationIds,
        onSuccess,
        interval,
        serviceId,
    });

    const { renderFormContent, hasDirtyFields, setValue, watch, dirtyFields } =
        useFormControls<IntervalAdjustmentFormValues>({
            defaultValues,
            dataTestForm: 'intervalAdjustment-form',
            onSubmit: (formValues: IntervalAdjustmentFormValues) => {
                changeInterval(formValues, dirtyFields);
            },
            resolver: createZodFormResolver(IntervalAdjustmentFormSchema),
        });

    useEffect(() => {
        setValue(IntervalAdjustmentFormKeys.Arrival, defaultValues[IntervalAdjustmentFormKeys.Arrival]);
        setValue(IntervalAdjustmentFormKeys.Departure, defaultValues[IntervalAdjustmentFormKeys.Departure]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultValues[IntervalAdjustmentFormKeys.Arrival], defaultValues[IntervalAdjustmentFormKeys.Departure]]);

    const arrival = watch(IntervalAdjustmentFormKeys.Arrival);
    const departure = watch(IntervalAdjustmentFormKeys.Departure);

    useEffect(() => {
        if (arrival && departure) {
            setInterval({ startUtc: arrival, endUtc: departure });
        }
    }, [arrival, departure]);

    return (
        <>
            {renderFormContent(
                <FormLayout submitButtonDisabled={!hasDirtyFields} loading={isPending} submitButtonLoading={loading}>
                    <DateTimeField
                        required
                        label={lt('Arrival')}
                        name={IntervalAdjustmentFormKeys.Arrival}
                        dateProps={{ minDate }}
                    />
                    <DateTimeField required label={lt('Departure')} name={IntervalAdjustmentFormKeys.Departure} />
                    {hasDirtyFields && <Checkboxes reservationIds={reservationIds} serviceId={serviceId} />}
                </FormLayout>
            )}
            {dialog}
        </>
    );
};

interface CheckboxesProps {
    reservationIds: string[];
    serviceId: string;
}

export const Checkboxes: FC<CheckboxesProps> = ({ reservationIds, serviceId }) => {
    const { watch, setValue } = useFormContext<IntervalAdjustmentFormValues>();
    const reservations = useCommanderSelector((state) =>
        getCurrentEnterpriseReservationsByIds(state, { ids: reservationIds })
    );

    const timezone = useTimezone();
    const calendarService = useCalendarService();

    const comparison = compareFormValuesForMultipleIntervals(
        watch(),
        reservations.map(({ startUtc, endUtc }) => ({ startUtc, endUtc })),
        timezone,
        calendarService
    );

    const createNewReservation = watch(IntervalAdjustmentFormKeys.CreateNewReservation);

    const enterpriseId = useCurrentEnterpriseId();

    const ratesOptions = useGroupedRatesOptions({
        serviceId,
        enterpriseId,
        filterRate: (rate: Rate) => rate.isEnabled && !rate.isDeleted && rate.type === RateType.Public,
    });

    useEffect(() => {
        setValue(IntervalAdjustmentFormKeys.ApplyCancellationFee, comparison.someNightsCanceled);
    }, [setValue, comparison.someNightsCanceled]);

    return (
        <>
            {/* to not mix rates we want to show this checkbox when reservation was prolonged to only one side */}
            {comparison.onlyOneSideOfIntervalWasExtended && (
                <Stack vertical spacing="100">
                    <FormCheckboxField
                        data-test-id="create-new-reservation-checkbox"
                        name={IntervalAdjustmentFormKeys.CreateNewReservation}
                    >
                        {lt('ReservationPropertiesTabArrivalFormCreateNewReservationWithRatesCheckbox')}
                    </FormCheckboxField>
                    {createNewReservation && (
                        <SelectField
                            options={ratesOptions.data ?? []}
                            label={lt('ReservationPropertiesTabArrivalFormCreateNewReservationRateDropdown')}
                            name={IntervalAdjustmentFormKeys.CreateNewReservationRateId}
                            showEmptyValue={false}
                            data-test-id="create-new-reservation-rate"
                        />
                    )}
                </Stack>
            )}
            {comparison.someNightsCanceled && (
                <FormCheckboxField name={IntervalAdjustmentFormKeys.ApplyCancellationFee}>
                    <CancellationFeeText
                        reservationIds={reservationIds}
                        startUtc={watch(IntervalAdjustmentFormKeys.Arrival)}
                        endUtc={watch(IntervalAdjustmentFormKeys.Departure)}
                    />
                </FormCheckboxField>
            )}
        </>
    );
};
